import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { isValid, parseISO } from 'date-fns';
import cn from '../../../utils/cn';
import { getContrastingColor } from '../../../utils/colors/colors';
import styles from './CalendarEventRenderer.module.css';
import Tooltip from '../../atoms/Tooltip/Tooltip';
import MarkdownContainer from '../../atoms/MarkdownContainer/MarkdownContainer';

/**
 * Format HH:MM from a Date
 * @param {Date} date
 * @returns {string}
 */
function formatEventTime(date) {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * @param {any} event
 * @returns {{ startTime: string, endTime: string }}
 */
function getEventTimeLabels(event) {
  const displayStart = event.extendedProps?.displayStart;
  const displayEnd = event.extendedProps?.displayEnd;
  if (displayStart) {
    const start = parseISO(displayStart);
    const end = displayEnd ? parseISO(displayEnd) : null;
    if (isValid(start)) {
      return {
        startTime: formatEventTime(start),
        endTime: end && isValid(end) ? formatEventTime(end) : '',
      };
    }
  }

  if (!event.start) {
    return { startTime: '', endTime: '' };
  }

  return {
    startTime: formatEventTime(event.start),
    endTime: event.end ? formatEventTime(event.end) : '',
  };
}

/**
 * First non-empty resource title for an event (timeline / timeGrid only)
 * @param {any} event
 * @param {boolean} isDayGridView
 * @returns {string}
 */
function getResourceTitle(event, isDayGridView) {
  if (isDayGridView || typeof event.getResources !== 'function') {
    return '';
  }
  const resources = event.getResources()
    .filter((/** @type {CalendarResource | null} */ r) => r !== null && r.title);
  return resources[0]?.title || '';
}

/**
 * CalendarEvent Component
 * Renders a calendar event with integrated tooltip management
 * @param {object} props
 * @param {any} props.eventInfo
 * @returns {JSX.Element | null}
 */
function CalendarEventRender({ eventInfo }) {
  const { t } = useTranslation();
  const [tooltip, setTooltip] = useState({
    isOpen: false,
    x: 0,
    y: 0,
  });

  const handleMouseEnter = useCallback((/** @type {React.MouseEvent} */ e) => {
    setTooltip({
      isOpen: true,
      x: e.clientX,
      y: e.clientY,
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTooltip({
      isOpen: false,
      x: 0,
      y: 0,
    });
  }, []);

  const { event, view } = eventInfo;
  const isLocked = !event?.extendedProps?.editable;

  if (!event?.start) {
    return null;
  }

  // used for month view with grid of day -> The title and hour must be on the same row
  const isDayGridView = view.type.includes('dayGrid');

  const { startTime, endTime } = getEventTimeLabels(event);

  const timeLabel = endTime
    ? t('fullCalendar.eventTimeRange', { start: startTime, end: endTime })
    : startTime;

  const eventAccent = event.backgroundColor
    || event.borderColor
    || event.extendedProps?.backgroundColor
    || 'var(--primary-700)';

  // The fallback CSS variable is a dark color: keep white text on it
  const eventText = /^#[0-9a-f]{6}$/i.test(eventAccent)
    ? getContrastingColor(eventAccent)
    : '#FFFFFF';

  const resourceTitle = getResourceTitle(event, isDayGridView);

  return (
    <>
      <div
        className={cn([
          styles.event,
          isLocked && styles.locked,
          isDayGridView && styles['time-grid-event']])}
        style={/** @type {React.CSSProperties & { '--event-accent': string, '--event-text': string }} */ ({
          '--event-accent': eventAccent,
          '--event-text': eventText,
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className={styles['event-title-row']}>
          <MarkdownContainer
            content={event.title}
            className={styles['event-title']}
            as="span"
          />
        </div>
        <p className={styles['event-time']}>{timeLabel}</p>
        {resourceTitle ? (
          <p className={styles['event-resource']}>{resourceTitle}</p>
        ) : null}
      </div>
      {tooltip.isOpen && createPortal(
        <Tooltip
          isOpen={tooltip.isOpen}
          x={tooltip.x}
          y={tooltip.y}
        >
          <MarkdownContainer
            content={event.title}
            className={styles['event-tooltip-content']}
          />
        </Tooltip>,
        document.body,
      )}
    </>
  );
}

/**
 * Wrapper function for FullCalendar eventContent
 * FullCalendar expects a function that receives eventInfo and returns JSX
 * @param {any} eventInfo
 * @returns {JSX.Element | null}
 */
function renderCalendarEvent(eventInfo) {
  return <CalendarEventRender eventInfo={eventInfo} />;
}

export default renderCalendarEvent;

// TODO LOIC : Remettre les tags en ayant trouvé un affichage acceptable sur petits events
// <div className={styles['event-tags']}>
//         {
//           eventInfo.event.extendedProps.tags?.map((/** @type {EventTag} */ tag) => (
//             <Tag key={tag.label} theme={tag.theme}>
//               <p>{tag.label}</p>
//             </Tag>
//           ))
//       }
//       </div>
